import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag } from 'lucide-react';
import axios from 'axios';
import { MDXProvider } from '@mdx-js/react';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`/api/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      // Fallback to mock data
      setPost({
        title: 'Sample Blog Post',
        content: '<h2>Welcome to this blog post</h2><p>This is a sample blog post. In production, this would be loaded from your API and rendered using MDX.</p>',
        tags: ['Tutorial', 'Web Development'],
        createdAt: new Date().toISOString(),
        author: { name: 'Admin' },
        readTime: '5 min read'
      });
    } finally {
      setLoading(false);
    }
  };

  const components = {
    h1: (props) => <h1 className="text-4xl font-display font-bold mb-6 mt-8" {...props} />,
    h2: (props) => <h2 className="text-3xl font-display font-bold mb-4 mt-6" {...props} />,
    h3: (props) => <h3 className="text-2xl font-display font-semibold mb-3 mt-4" {...props} />,
    p: (props) => <p className="mb-4 text-lg leading-relaxed text-gray-700 dark:text-gray-300" {...props} />,
    ul: (props) => <ul className="list-disc list-inside mb-4 space-y-2" {...props} />,
    ol: (props) => <ol className="list-decimal list-inside mb-4 space-y-2" {...props} />,
    li: (props) => <li className="text-gray-700 dark:text-gray-300" {...props} />,
    code: (props) => <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm font-mono" {...props} />,
    pre: (props) => <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto mb-4" {...props} />,
    blockquote: (props) => (
      <blockquote className="border-l-4 border-[rgb(var(--color-accent))] pl-4 italic my-4 text-gray-600 dark:text-gray-400" {...props} />
    ),
    a: (props) => <a className="text-[rgb(var(--color-accent))] hover:underline" {...props} />,
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(var(--color-accent))]"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Post not found</h2>
          <Link to="/blog" className="text-[rgb(var(--color-accent))] hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24">
      <div className="container-custom max-w-4xl">
        {/* Back Button */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="mb-8"
        >
          <Link
            to="/blog"
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-[rgb(var(--color-accent))] transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>
        </motion.div>

        {/* Article Header */}
        <motion.header
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags?.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm"
              >
                <Tag size={14} className="mr-1" />
                {tag}
              </span>
            ))}
          </div>

          <h1 className="mb-6">{post.title}</h1>

          <div className="flex items-center text-gray-600 dark:text-gray-400 space-x-4">
            <span className="flex items-center">
              <Calendar size={18} className="mr-2" />
              {new Date(post.createdAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
            {post.readTime && (
              <>
                <span>•</span>
                <span className="flex items-center">
                  <Clock size={18} className="mr-2" />
                  {post.readTime}
                </span>
              </>
            )}
            {post.author && (
              <>
                <span>•</span>
                <span>By {post.author.name}</span>
              </>
            )}
          </div>
        </motion.header>

        {/* Article Content */}
        <motion.article
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.2 }}
          className="prose prose-lg dark:prose-invert max-w-none"
        >
          <MDXProvider components={components}>
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </MDXProvider>
        </motion.article>

        {/* Share Section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800"
        >
          <p className="text-gray-600 dark:text-gray-400 mb-4">Share this article:</p>
          <div className="flex gap-4">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Twitter
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPost;
